/* Angular Modules */
import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Router} from '@angular/router';
/* External Libraries */
import {Observable, Subscription, of} from 'rxjs';
import {map, catchError} from 'rxjs/operators';
/* Local Models */
/* Local Services */
import {MessageService} from './message.service';

import {environment} from '@environments/environment';
import {AccountService} from '@app/modules/operations-api/services';
import {User} from '@app/modules/operations-api/models';

@Injectable()
export class AuthService  {

  constructor(
    private http: HttpClient,
    private router: Router,
    private messages: MessageService,
    private userApi: AccountService,
  ) {
    this.messages.setPage("Account");
  }

  static _user: User;

  static get User() {
    if (!AuthService._user && window.sessionStorage) {
      const storage = window.sessionStorage.getItem('operations_user');
      if (storage) {
        AuthService._user = JSON.parse(storage);
      }
    }
    return AuthService._user;
  }

  static set User(value) {
    window.sessionStorage.setItem('operations_user', JSON.stringify(value));
    AuthService._user = value;
  }

  endpoint = environment.webapi;
  user$: Subscription;
  private storeSubscription: Subscription;

  hasClaim(key: string): boolean {
    return (AuthService._user.claims.filter(x => x.name === key).length > 0);
  }
  hasRole(key: string): boolean {
    return (AuthService._user.rolename === key);
  }
  login(username: string, password: string): Observable<User> {
    try {
      return this.userApi.AccountLogin({
        username: username,
        password: password,
        newPassword: '',
        applicationName: ''
      }).pipe(
        catchError(ex => {
          try {
            console.log(ex.error.ExceptionMessage ? ex.error.ExceptionMessage : '');
          } catch (e) {
          }
          // @ts-ignore
          this.messages.error("Account Login",
            "There was a problem logging into your account.", true, false, false);

          return of(null as User);
        }),
        map((userData: User) => {
          // @ts-ignore
          if (userData) {
            if (userData.accessToken) {
              this.messages.success("Account Login",
                "You have logged into the system.", true, true, false);
              AuthService.User = userData;
              return userData;
            }
          }
        }),
      );
    } catch (e) {
    }
  }
  forgotPassword(username: string): Observable<any> {
    return this.userApi.AccountForgotUserPassword(username).pipe(
      map(data => {
        return data;
      }
      )
    );
  }
  impersonateUser(username: string, password: string): Observable<User> {
    return this.http
      .post<User>(this.endpoint + 'users/impersonate',
        {}, {
          params: {
            'impersonateUser': username,
            'password': password
          }
        })
      .pipe(
        catchError(ex => {
          // @ts-ignore
          const error = (ex.error.ExceptionMessage ? ex.error.ExceptionMessage : '');
          // @ts-ignore
          this.messages.error("Impersonate User",
            "There was a problem impersonating the user account entered. Either the email address entered is incorrect or the password entered is incorrect." + '<br/>' + error, true, false, false);

          return of(null as User);
        }),
        map(userData => {
            // @ts-ignore
            const usr = userData.Value;
            // @ts-ignore
            if (userData.IsFailure) {
              // @ts-ignore
              this.messages.error("Impersonate User",
                "There was a problem impersonating the user account entered. Either the email address entered is incorrect or the password entered is incorrect.", true, false, false);
            } else {
              if (usr && usr.AccessToken) {
                this.messages.success("Impersonate User",
                  "The impersonation has completed successfully. You will now be viewing the Dashboard with the requested account.", true, false, false);
                AuthService.User = usr;

                return userData;
              }
            }
          }
        )
      );
  }
  validateResetPassword(username: string, uniquecode: string): Observable<any> {
    return this.userApi.AccountValidateResetPassword({ username: username, code: uniquecode }).pipe(
      map(data => {
        return data;
      }
      )
    );
  }
  resetPassword(username: string, password: string, confirmpassword: string, uniquecode: string): Observable<User> {
    const user = {
      'username': username,
      'password': password,
      'applicationName': environment.application,
      'newPassword': confirmpassword
    };

    return this.userApi.AccountResetForgottenPassword({ user, code: uniquecode })
      .pipe(
        catchError(ex => {
          // @ts-ignore
          const error = (ex.error.ExceptionMessage ? ex.error.ExceptionMessage : '');
          // @ts-ignore
          this.messages.error('Password Reset',
            'There was a problem resetting your password.' + '<br/>' + error, true, false, false);
          return of(null as User);
        }),
        map(data => {
          // @ts-ignore
          if (data.IsFailure) {
            // @ts-ignore
            this.messages.error('Password Reset', data.Error, true, false, false);
          } else {
            // @ts-ignore
            return data;
          }
        })
    );
  }
  changePassword(username: string, password: string, confirmpassword: string): Observable<User> {
    return this.http.post<User>(this.endpoint + 'users/password/reset',
      {
        'Username': username,
        'Password': password,
        'ApiKey': environment.apikey,
        'NewPassword': confirmpassword
      })
      .pipe(
        catchError(ex => {
          // @ts-ignore
          const error = (ex.error.ExceptionMessage ? ex.error.ExceptionMessage : '');
          // @ts-ignore
          this.messages.error("Password Reset",
            "There was a problem resetting your password." + '<br/>' + error, true, false, false);

          return of(null as User);
        }),
        map(data => {
          // @ts-ignore
          if (data.IsFailure) {
            // @ts-ignore
            this.messages.error("Password Reset", data.Error, true, false, false);
          } else {
            return data;
          }
        }));
  }
  isAuthenticated(): boolean {
    if (AuthService.User && this.getAuthToken()) {
      return true;
    }
    return false;
  }
  logout() {
    window.sessionStorage.removeItem('operations_user');
    AuthService._user = null;
    setTimeout(() => this.router.navigate(['/login']), 100);
  }
  refreshToken(): Observable<User> {
    return this.userApi.AccountRefreshToken(AuthService.User)
      .pipe(
        map((user$: User) => {
          // pull the current user from the local storage
          const storedUser: User = AuthService.User;
          if (user$ && user$.accessToken) {
            // update the access token
            storedUser.accessToken = user$.accessToken;
            // push the current user back to the local storage
            // this.store.dispatch(new AccountState.SetUser(storedUser));
          }
          return storedUser;
        })
      );
  }
  getAuthToken(): string {
    const user = AuthService.User;
    if (user) {
      return user.accessToken;
    }
    return '';
  }

}
